import { useState, useCallback } from 'react';
import {
  getBudgetHealthThresholds,
  saveBudgetHealthThresholds,
  DEFAULT_BUDGET_HEALTH_THRESHOLDS,
  type BudgetHealthThresholds,
} from '@/lib/budget-health-thresholds-storage';

export interface UseBudgetHealthThresholdsReturn {
  thresholds: BudgetHealthThresholds;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateThreshold: (key: keyof Omit<BudgetHealthThresholds, 'lastUpdated'>, value: number | boolean) => void;
  saveThresholds: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useBudgetHealthThresholds(): UseBudgetHealthThresholdsReturn {
  const [thresholds, setThresholds] = useState<BudgetHealthThresholds>(getBudgetHealthThresholds);
  const [savedThresholds, setSavedThresholds] = useState<BudgetHealthThresholds>(getBudgetHealthThresholds);
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    thresholds.costAmberThreshold !== savedThresholds.costAmberThreshold ||
    thresholds.costRedThreshold !== savedThresholds.costRedThreshold ||
    thresholds.scheduleAmberGap !== savedThresholds.scheduleAmberGap ||
    thresholds.scheduleRedGap !== savedThresholds.scheduleRedGap ||
    thresholds.hoursAmberGap !== savedThresholds.hoursAmberGap ||
    thresholds.hoursRedGap !== savedThresholds.hoursRedGap ||
    thresholds.wipAmberThreshold !== savedThresholds.wipAmberThreshold ||
    thresholds.wipRedThreshold !== savedThresholds.wipRedThreshold ||
    thresholds.billingAmberGap !== savedThresholds.billingAmberGap ||
    thresholds.billingRedGap !== savedThresholds.billingRedGap ||
    thresholds.milestoneRedOnAnyOverdue !== savedThresholds.milestoneRedOnAnyOverdue ||
    // Dimension 6 — Margin (Task 22)
    thresholds.marginAmberThreshold !== savedThresholds.marginAmberThreshold ||
    thresholds.marginRedThreshold !== savedThresholds.marginRedThreshold ||
    thresholds.marginEnabled !== savedThresholds.marginEnabled;

  const updateThreshold = useCallback(
    (key: keyof Omit<BudgetHealthThresholds, 'lastUpdated'>, value: number | boolean) => {
      setThresholds((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveThresholds = useCallback(async () => {
    setIsSaving(true);
    try {
      // Simulate async save (replace with real API call when backend is ready)
      await new Promise((resolve) => setTimeout(resolve, 400));
      saveBudgetHealthThresholds(thresholds);
      setSavedThresholds(thresholds);
    } finally {
      setIsSaving(false);
    }
  }, [thresholds]);

  const resetToDefaults = useCallback(() => {
    setThresholds({ ...DEFAULT_BUDGET_HEALTH_THRESHOLDS });
  }, []);

  return {
    thresholds,
    hasUnsavedChanges,
    isSaving,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  };
}
